import { BaseService } from "./base.service";
import {
  LabInsightsAllGroups,
  LabInsightsGroupDetails,
  LabInsightsLanding,
  LabInsightsParameterDetails,
  LabParameterGroupId,
  PinnableParameters,
  PinnedParameterCard,
  SetPinnedParametersRequest,
} from "@/types/lab-insights";

class LabInsightsService extends BaseService {
  constructor() {
    super("/lab-interpretations");
  }

  async getLanding(memberId: string): Promise<LabInsightsLanding> {
    return this.get<LabInsightsLanding>(`/${memberId}/landing`);
  }

  async getAllGroups(memberId: string): Promise<LabInsightsAllGroups> {
    return this.get<LabInsightsAllGroups>(`/${memberId}/all-groups`);
  }

  async getGroupDetails(
    memberId: string,
    groupId: LabParameterGroupId
  ): Promise<LabInsightsGroupDetails> {
    return this.get<LabInsightsGroupDetails>(
      `/${memberId}/groups/${groupId}`
    );
  }

  async getParameterDetails(
    memberId: string,
    canonicalParameterId: string
  ): Promise<LabInsightsParameterDetails> {
    return this.get<LabInsightsParameterDetails>(
      `/${memberId}/parameters/${canonicalParameterId}`
    );
  }

  /**
   * Screen 3 for an unmatched OTHERS-bucket parameter — keyed on the raw OCR
   * parameter name instead of a canonical id. Hits the same endpoint with the
   * `_raw` sentinel path id plus `raw` + `group` query params.
   */
  async getParameterDetailsByRawName(
    memberId: string,
    rawName: string,
    groupId: string
  ): Promise<LabInsightsParameterDetails> {
    return this.get<LabInsightsParameterDetails>(
      `/${memberId}/parameters/_raw?raw=${encodeURIComponent(
        rawName
      )}&group=${encodeURIComponent(groupId)}`
    );
  }

  async setPins(
    memberId: string,
    canonicalParameterIds: string[]
  ): Promise<PinnedParameterCard[]> {
    const body: SetPinnedParametersRequest = { canonicalParameterIds };
    return this.put<PinnedParameterCard[]>(`/${memberId}/pins`, body);
  }

  async getPinnableParameters(memberId: string): Promise<PinnableParameters> {
    return this.get<PinnableParameters>(`/${memberId}/pinnable-parameters`);
  }

  /**
   * Download a parameter's past-results PDF as a Blob. Used both as a fallback
   * for the web Share API and as the underlying transport when the native
   * webview share bridge is unavailable.
   */
  async downloadParameterPdf(
    memberId: string,
    canonicalParameterId: string
  ): Promise<Blob> {
    return this.get<Blob>(
      `/${memberId}/parameters/${canonicalParameterId}/pdf`,
      { responseType: "blob" }
    );
  }
}

export const labInsightsService = new LabInsightsService();
export default labInsightsService;
